import React, { useState, useRef, useEffect } from 'react';
import { 
  Upload, ZoomIn, ZoomOut, Bookmark, Plus, Trash2, X, 
  ScanLine, Aperture, Zap, Eye, BarChart2, Sun
} from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

const Temp_sergio = () => {
  // --- Configuración Inicial ---
  const DEFAULT_IMAGE = "https://team.tomi.la/storage/sample.jpg";

  // Estado de la Imagen y Transformación
  const [imageSrc, setImageSrc] = useState(DEFAULT_IMAGE);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  
  // --- NUEVOS ESTADOS DE CONTROL DE MÁQUINA ---
  const [laserMode, setLaserMode] = useState('OFF'); // OFF, CW, FS
  const [laserIntensity, setLaserIntensity] = useState(50);
  const [visionMode, setVisionMode] = useState('COLOR'); // COLOR, SCAN
  const [focusLevel, setFocusLevel] = useState(100); // 0 (Blurry) - 100 (Sharp)
  const [brightness, setBrightness] = useState(100); // 0% - 200%

  // Estados UI
  const [savedViews, setSavedViews] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  // Referencias
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastPinchDistanceRef = useRef(null);

  // Constantes
  const MIN_SCALE = 0.2;
  const MAX_SCALE = 120; 
  const ZOOM_SENSITIVITY = 0.001;

  // --- Lógica de Imagen (Filtros dinámicos) ---
  const getImageStyle = () => {
    let filters = [];
    
    // 1. Enfoque
    const blurAmount = (100 - focusLevel) / 10; 
    filters.push(`blur(${blurAmount}px)`);

    // 2. Brillo (Brightness)
    filters.push(`brightness(${brightness}%)`);

    // 3. Vision Mode
    if (visionMode === 'SCAN') {
      filters.push('grayscale(100%) sepia(100%) hue-rotate(90deg) contrast(150%)');
    }

    return {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
      filter: filters.join(' '),
      transition: 'filter 0.2s ease-out'
    };
  };

  // --- Efecto de Láser (Overlay de luz) ---
  const getLaserOverlayStyle = () => {
    if (laserMode === 'OFF') return { opacity: 0 };
    
    const opacity = laserIntensity / 200; 
    const color = laserMode === 'CW' ? '255, 0, 0' : '0, 255, 255'; // Rojo para CW, Cyan para FS

    return {
      background: `radial-gradient(circle at 50% 50%, rgba(${color}, ${opacity}) 0%, rgba(${color}, 0) 70%)`,
      opacity: 1,
      mixBlendMode: 'screen'
    };
  };

  // --- Manejo de Carga ---
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
        setTransform({ x: 0, y: 0, scale: 1 });
      };
      reader.readAsDataURL(file);
    }
  };

  // --- Gestión de Vistas ---
  const saveCurrentView = () => {
    const newView = {
      id: Date.now(),
      label: `POS-${savedViews.length + 1}`,
      transform: { ...transform },
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setSavedViews([...savedViews, newView]);
  };

  const restoreView = (viewTransform) => setTransform(viewTransform);
  
  const deleteView = (id, e) => {
    e.stopPropagation();
    setSavedViews(savedViews.filter(v => v.id !== id));
  };

  // --- Zoom Logic ---
  const applyZoom = (delta, cursorX, cursorY) => {
    setTransform((prev) => {
      const newScale = Math.min(Math.max(prev.scale + delta, MIN_SCALE), MAX_SCALE);
      if (newScale === prev.scale) return prev;
      const scaleRatio = newScale / prev.scale;
      const newX = cursorX - (cursorX - prev.x) * scaleRatio;
      const newY = cursorY - (cursorY - prev.y) * scaleRatio;
      return { x: newX, y: newY, scale: newScale };
    });
  };

  const setPhysicalZoom = (zoomValue) => {
    // Zoom al centro de la pantalla
    const baseScale = zoomValue / 5; 
    setTransform(prev => ({ ...prev, scale: baseScale }));
  };

  // --- Event Handlers (Mouse/Touch) ---
  const handlePointerDown = (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
    if (containerRef.current) containerRef.current.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    if (lastPinchDistanceRef.current !== null) return;
    setTransform((prev) => ({
      ...prev,
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    }));
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    lastPinchDistanceRef.current = null;
    if (containerRef.current) containerRef.current.releasePointerCapture(e.pointerId);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * ZOOM_SENSITIVITY * transform.scale;
    applyZoom(delta, e.clientX, e.clientY);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        const t1 = e.touches[0]; const t2 = e.touches[1];
        lastPinchDistanceRef.current = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      }
    };
    const handleTouchMove = (e) => {
        if (e.touches.length === 2 && lastPinchDistanceRef.current !== null) {
          e.preventDefault();
          const t1 = e.touches[0]; const t2 = e.touches[1];
          const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
          const cx = (t1.clientX + t2.clientX) / 2;
          const cy = (t1.clientY + t2.clientY) / 2;
          const delta = (dist - lastPinchDistanceRef.current) * 0.005 * transform.scale;
          applyZoom(delta, cx, cy);
          lastPinchDistanceRef.current = dist;
        }
    };
    const handleTouchEnd = () => { lastPinchDistanceRef.current = null; };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [transform.scale, transform.x, transform.y]);

  // Estilos CSS personalizados
  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000000',
    color: '#d1d5db',
    overflow: 'hidden',
    fontFamily: 'monospace',
    userSelect: 'none'
  };

  const viewerStyle = {
    position: 'relative',
    flex: 1,
    backgroundColor: '#0a0a0a',
    overflow: 'hidden',
    cursor: 'crosshair',
    touchAction: 'none'
  };

  const imageWrapperStyle = {
    position: 'absolute',
    transformOrigin: 'top left',
    willChange: 'transform',
    left: '50%',
    top: '50%',
    marginLeft: imageRef.current ? -(imageRef.current.width / 2) : 0,
    marginTop: imageRef.current ? -(imageRef.current.height / 2) : 0
  };

  const bottomBarStyle = {
    position: 'absolute',
    bottom: '24px',
    left: '24px',
    right: '288px',
    height: '56px',
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    border: '1px solid #374151',
    backdropFilter: 'blur(8px)',
    borderRadius: '9999px',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 50,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  const controlPanelStyle = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '288px',
    height: '100%',
    backgroundColor: '#111111',
    borderLeft: '1px solid #1f2937',
    zIndex: 50,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-10px 0 20px rgba(0, 0, 0, 0.5)'
  };

  const borderColor = visionMode === 'SCAN' ? '#4ade80' : 'rgba(239, 68, 68, 0.5)';
  const textColor = visionMode === 'SCAN' ? '#4ade80' : '#f87171';

  // --- Render ---
  return (
    <div style={containerStyle} className="d-flex">
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .custom-scrollbar::-webkit-scrollbar { 
          width: 4px; 
        }
        .custom-scrollbar::-webkit-scrollbar-track { 
          background: #111111; 
        }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: #333; 
          border-radius: 2px; 
        }
        input[type=range] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        input[type=range]::-webkit-slider-track {
          height: 4px;
          border-radius: 4px;
        }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: currentColor;
          margin-top: -4px;
        }
        input[type=range]::-moz-range-thumb {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: currentColor;
          border: none;
        }
        input[type=range]::-moz-range-track {
          height: 4px;
          border-radius: 4px;
        }
      `}</style>
      
      {/* 1. VISOR PRINCIPAL */}
      <div 
        ref={containerRef}
        style={viewerStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Capa de Imagen */}
        <div style={{...imageWrapperStyle, ...getImageStyle()}}>
          <img 
            ref={imageRef}
            src={imageSrc} 
            alt="Inspection Surface" 
            style={{
              pointerEvents: 'none',
              maxWidth: 'none'
            }}
            onLoad={(e) => { e.target.style.transform = "translate(-50%, -50%)"; }}
          />
        </div>

        {/* Capa de Efecto Láser */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.3s',
            ...getLaserOverlayStyle()
          }} 
        />

        {/* OVERLAY HUD */}
        {showOverlay && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
            {/* Esquinas */}
            <div style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              width: '48px',
              height: '48px',
              borderTop: `1px solid ${borderColor}`,
              borderLeft: `1px solid ${borderColor}`
            }}></div>
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '48px',
              height: '48px',
              borderTop: `1px solid ${borderColor}`,
              borderRight: `1px solid ${borderColor}`
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '16px',
              width: '48px',
              height: '48px',
              borderBottom: `1px solid ${borderColor}`,
              borderLeft: `1px solid ${borderColor}`
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              width: '48px',
              height: '48px',
              borderBottom: `1px solid ${borderColor}`,
              borderRight: `1px solid ${borderColor}`
            }}></div>

            {/* Crosshair Dinámico */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0.7
            }}>
              <div style={{
                width: '32px',
                height: '1px',
                marginBottom: '8px',
                marginLeft: 'auto',
                marginRight: 'auto',
                backgroundColor: borderColor
              }}></div>
              <div style={{
                height: '32px',
                width: '1px',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: borderColor
              }}></div>
            </div>

            {/* Data Block */}
            <div style={{
              position: 'absolute',
              top: '24px',
              left: '32px',
              fontSize: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: textColor
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: laserMode !== 'OFF' ? 'currentColor' : '#374151',
                  animation: laserMode !== 'OFF' ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                }}></div>
                <span>LASER: {laserMode} {laserMode !== 'OFF' && `${laserIntensity}%`}</span>
              </div>
              <div style={{ color: '#6b7280' }}>
                X: {transform.x.toFixed(1)} | Y: {transform.y.toFixed(1)} | B: {brightness}%
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2. BARRA INFERIOR (NAVEGACIÓN) */}
      <div style={bottomBarStyle}>
        <div className="d-flex align-items-center" style={{ gap: '16px' }}>
          <button 
            onClick={() => setShowOverlay(!showOverlay)} 
            style={{
              padding: '8px',
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              color: showOverlay ? '#4ade80' : '#6b7280',
              backgroundColor: showOverlay ? 'rgba(34, 197, 94, 0.2)' : 'transparent'
            }}
          >
            <ScanLine size={18} />
          </button>
          <div style={{ width: '1px', height: '24px', backgroundColor: '#374151' }}></div>
          
          {/* Zoom Digital Manual */}
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <button 
              onClick={() => applyZoom(-0.5, window.innerWidth/2, window.innerHeight/2)} 
              style={{
                padding: '8px',
                border: 'none',
                background: 'transparent',
                color: '#d1d5db',
                borderRadius: '50%'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1f2937'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <ZoomOut size={18}/>
            </button>
            <span style={{
              fontSize: '12px',
              fontWeight: 'bold',
              width: '48px',
              textAlign: 'center',
              color: '#60a5fa'
            }}>
              {(transform.scale * 5).toFixed(1)}x
            </span>
            <button 
              onClick={() => applyZoom(0.5, window.innerWidth/2, window.innerHeight/2)} 
              style={{
                padding: '8px',
                border: 'none',
                background: 'transparent',
                color: '#d1d5db',
                borderRadius: '50%'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1f2937'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <ZoomIn size={18}/>
            </button>
          </div>
        </div>

        <div className="d-flex align-items-center" style={{ gap: '16px' }}>
          {/* Bookmarks reducido */}
          <div style={{ position: 'relative' }}>
            <button 
              onClick={() => setShowBookmarks(!showBookmarks)} 
              className={`d-flex align-items-center ${showBookmarks ? 'bg-primary text-white' : 'bg-secondary'}`}
              style={{
                gap: '8px',
                padding: '6px 12px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 'bold',
                border: 'none',
                transition: 'all 0.2s',
                backgroundColor: showBookmarks ? '#2563eb' : '#1f2937',
                color: showBookmarks ? '#ffffff' : '#60a5fa'
              }}
              onMouseEnter={(e) => {
                if (!showBookmarks) e.target.style.backgroundColor = '#374151';
              }}
              onMouseLeave={(e) => {
                if (!showBookmarks) e.target.style.backgroundColor = '#1f2937';
              }}
            >
              <Bookmark size={14} />
              <span>LOCATIONS</span>
            </button>
            {/* Pop-up Menú */}
            {showBookmarks && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                marginBottom: '16px',
                width: '256px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '8px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                zIndex: 1000
              }}>
                <div style={{
                  maxHeight: '192px',
                  overflowY: 'auto',
                  marginBottom: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {savedViews.map(v => (
                    <div 
                      key={v.id} 
                      onClick={() => restoreView(v.transform)} 
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        borderRadius: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1f2937'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <span>{v.label}</span>
                      <Trash2 
                        size={12} 
                        onClick={(e) => deleteView(v.id, e)} 
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                        onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                      />
                    </div>
                  ))}
                </div>
                <button 
                  onClick={saveCurrentView} 
                  className="btn btn-primary w-100"
                  style={{
                    padding: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    backgroundColor: '#2563eb'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
                >
                  <Plus size={12}/> SAVE POS
                </button>
              </div>
            )}
          </div>

          <label style={{
            cursor: 'pointer',
            backgroundColor: '#1f2937',
            padding: '8px',
            borderRadius: '50%',
            transition: 'background-color 0.2s',
            display: 'inline-block'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#374151'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#1f2937'}
          >
            <input 
              type="file" 
              style={{ display: 'none' }} 
              onChange={handleImageUpload} 
              accept="image/*" 
            />
            <Upload size={18} style={{ color: '#d1d5db' }}/>
          </label>
        </div>
      </div>

      {/* 3. PANEL DE CONTROL LATERAL (MÁQUINA) */}
      <div style={controlPanelStyle}>
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #1f2937',
          backgroundColor: '#161616'
        }}>
          {/* Logo */}
          <div style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src="https://img1.wsimg.com/isteam/ip/01b4bec0-a46f-4dba-88d7-303093b83447/blob-14a240b.png/:/rs=w:701,h:286,cg:true,m/cr=w:701,h:286/qt=q:95"
              alt="Logo"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '60px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h2 style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#9ca3af',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: 0
          }}>
            <Zap size={14} style={{ color: '#eab308' }}/> Instrument Control
          </h2>
        </div>

        <div className="custom-scrollbar" style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          
          {/* SECCIÓN 1: ZOOM MECÁNICO */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="d-flex justify-content-between align-items-center">
              <label style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#6b7280',
                textTransform: 'uppercase'
              }}>
                Mechanical Zoom
              </label>
              <span style={{
                fontSize: '10px',
                color: '#60a5fa',
                fontFamily: 'monospace'
              }}>
                LENS: ACTIVE
              </span>
            </div>
            <div className="row g-2">
              {[5, 10, 40, 60].map((z) => {
                const isActive = Math.abs((transform.scale * 5) - z) < 0.5;
                return (
                  <div key={z} className="col-6">
                    <button 
                      onClick={() => setPhysicalZoom(z)} 
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        border: `1px solid ${isActive ? '#3b82f6' : '#374151'}`,
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'rgba(31, 41, 55, 0.5)',
                        color: isActive ? '#60a5fa' : '#9ca3af',
                        boxShadow: isActive ? '0 0 10px rgba(59, 130, 246, 0.3)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.target.style.borderColor = '#4b5563';
                          e.target.style.backgroundColor = 'rgba(31, 41, 55, 0.8)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.target.style.borderColor = '#374151';
                          e.target.style.backgroundColor = 'rgba(31, 41, 55, 0.5)';
                        }
                      }}
                    >
                      {z}x
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* SECCIÓN 2: ENFOQUE */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="d-flex justify-content-between align-items-center">
              <label style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#6b7280',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Aperture size={12}/> Focus
              </label>
              <span style={{
                fontSize: '10px',
                color: '#9ca3af',
                fontFamily: 'monospace'
              }}>
                {focusLevel}%
              </span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={focusLevel} 
              onChange={(e) => setFocusLevel(Number(e.target.value))}
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#374151',
                borderRadius: '4px',
                outline: 'none',
                accentColor: '#3b82f6'
              }}
            />
          </section>

          {/* SECCIÓN 2.5: BRILLO */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="d-flex justify-content-between align-items-center">
              <label style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#6b7280',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Sun size={12}/> Brightness
              </label>
              <span style={{
                fontSize: '10px',
                color: '#fbbf24',
                fontFamily: 'monospace'
              }}>
                {brightness}%
              </span>
            </div>
            <input 
              type="range" 
              min="0" 
              max="200" 
              value={brightness} 
              onChange={(e) => setBrightness(Number(e.target.value))}
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#374151',
                borderRadius: '4px',
                outline: 'none',
                accentColor: '#fbbf24'
              }}
            />
          </section>

          {/* SECCIÓN 3: VISION MODE */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#6b7280',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Eye size={12}/> Vision Sensor
            </label>
            <div style={{
              display: 'flex',
              backgroundColor: '#1f2937',
              borderRadius: '4px',
              padding: '4px',
              border: '1px solid #374151'
            }}>
              <button 
                onClick={() => setVisionMode('COLOR')}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  border: 'none',
                  backgroundColor: visionMode === 'COLOR' ? '#4b5563' : 'transparent',
                  color: visionMode === 'COLOR' ? '#ffffff' : '#6b7280'
                }}
                onMouseEnter={(e) => {
                  if (visionMode !== 'COLOR') e.target.style.color = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  if (visionMode !== 'COLOR') e.target.style.color = '#6b7280';
                }}
              >
                COLOR
              </button>
              <button 
                onClick={() => setVisionMode('SCAN')}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  border: 'none',
                  backgroundColor: visionMode === 'SCAN' ? '#15803d' : 'transparent',
                  color: visionMode === 'SCAN' ? '#ffffff' : '#6b7280',
                  boxShadow: visionMode === 'SCAN' ? '0 0 10px rgba(21, 128, 61, 0.5)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (visionMode !== 'SCAN') e.target.style.color = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  if (visionMode !== 'SCAN') e.target.style.color = '#6b7280';
                }}
              >
                SCAN
              </button>
            </div>
          </section>

          {/* SECCIÓN 4: LASER CONTROL */}
          <section style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '12px',
            border: '1px solid #1f2937',
            borderRadius: '4px',
            backgroundColor: 'rgba(17, 24, 39, 0.3)'
          }}>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '8px' }}>
              <label style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#6b7280',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <BarChart2 size={12}/> Laser Emitter
              </label>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: laserMode !== 'OFF' ? '#ef4444' : '#1f2937',
                animation: laserMode !== 'OFF' ? 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite' : 'none'
              }}></div>
            </div>
            
            {/* Botones de Modo */}
            <div className="d-flex" style={{ gap: '8px', marginBottom: '16px' }}>
              {['OFF', 'CW', 'FS'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setLaserMode(mode)}
                  style={{
                    flex: 1,
                    padding: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    border: `1px solid ${laserMode === mode 
                      ? (mode === 'OFF' ? '#6b7280' : '#ef4444') 
                      : '#374151'
                    }`,
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    backgroundColor: laserMode === mode 
                      ? (mode === 'OFF' ? '#374151' : 'rgba(239, 68, 68, 0.3)') 
                      : 'transparent',
                    color: laserMode === mode 
                      ? (mode === 'OFF' ? '#d1d5db' : '#f87171') 
                      : '#6b7280',
                    borderColor: laserMode === mode 
                      ? (mode === 'OFF' ? '#6b7280' : '#ef4444') 
                      : '#374151'
                  }}
                  onMouseEnter={(e) => {
                    if (laserMode !== mode) {
                      e.target.style.backgroundColor = '#1f2937';
                      e.target.style.borderColor = '#4b5563';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (laserMode !== mode) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#374151';
                    }
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* Intensidad */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className="d-flex justify-content-between" style={{ fontSize: '10px', color: '#6b7280' }}>
                <span>PWR</span>
                <span>{laserIntensity}%</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="100" 
                disabled={laserMode === 'OFF'}
                value={laserIntensity} 
                onChange={(e) => setLaserIntensity(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: '4px',
                  borderRadius: '4px',
                  outline: 'none',
                  backgroundColor: laserMode === 'OFF' ? '#1f2937' : 'rgba(239, 68, 68, 0.5)',
                  accentColor: '#ef4444',
                  opacity: laserMode === 'OFF' ? 0.5 : 1,
                  cursor: laserMode === 'OFF' ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </section>
        </div>
        
        {/* Footer del Panel */}
        <div style={{
          padding: '12px',
          backgroundColor: '#0a0a0a',
          borderTop: '1px solid #1f2937',
          fontSize: '9px',
          color: '#4b5563',
          textAlign: 'center',
          fontFamily: 'monospace'
        }}>
          SYSTEM READY<br/>V.2.0.5-IND
        </div>
      </div>
    </div>
  );
};

export default Temp_sergio;
